import { neighborsFor } from '../../game/geometry';
import { isChaosVariant, type BoardSize, type GameConfig } from '../../shared/config';
import {
  isDebuffKind,
  isSpawnLive,
  type ActiveEffect,
  type PowerUpKind,
  type PowerUpSpawn,
  type TimedEffectKind,
} from '../../shared/power-ups';
import type { PowerUpMessage } from '../../shared/protocol';
import { powerUpSoundEvent, type SoundEvent } from '../audio/sound-pack';
import { POWER_UP_INFO } from '../content/power-ups';
import { kebab, POWER_UP_TILE_PROPERTIES, powerUpTileProperty } from '../themes/power-up-tokens';

/** Pure chaos presentation decisions: what is on the board now, what applies to me, and the copy. */

export interface EffectStatus {
  kind: TimedEffectKind;
  remainingMs: number;
}

/** Everything the play screen shows for chaos, derived from the snapshot and the server clock. */
export interface ChaosBoardState {
  active: boolean;
  /** Visible spawns while this participant can play; empty otherwise. */
  spawns: PowerUpSpawn[];
  effects: EffectStatus[];
  frozen: boolean;
  spun: boolean;
  fogged: boolean;
}

const INACTIVE_CHAOS: ChaosBoardState = {
  active: false,
  spawns: [],
  effects: [],
  frozen: false,
  spun: false,
  fogged: false,
};

export function chaosBoardState(input: {
  config: Pick<GameConfig, 'variant'> | null;
  powerUps: readonly Readonly<PowerUpSpawn>[];
  effects: readonly Readonly<ActiveEffect>[];
  now: number;
  canPlay: boolean;
}): ChaosBoardState {
  if (!isChaosVariant(input.config)) return INACTIVE_CHAOS;
  const { effects, now } = input;
  return {
    active: true,
    spawns: input.canPlay ? liveSpawns(input.powerUps, now) : [],
    effects: effectStatuses(effects, now),
    frozen: hasActiveEffect(effects, 'freeze', now),
    spun: hasActiveEffect(effects, 'spin', now),
    fogged: hasActiveEffect(effects, 'fog', now),
  };
}

/** Spawns visible at `now` (server milliseconds): announced, not expired, not taken. */
export function liveSpawns(spawns: readonly Readonly<PowerUpSpawn>[], now: number): PowerUpSpawn[] {
  return spawns.filter((spawn) => isSpawnLive(spawn, now));
}

export function effectStatuses(effects: readonly Readonly<ActiveEffect>[], now: number): EffectStatus[] {
  return effects
    .filter((effect) => effect.until > now)
    .map((effect) => ({ kind: effect.kind, remainingMs: effect.until - now }))
    .sort((a, b) => a.remainingMs - b.remainingMs);
}

export function hasActiveEffect(
  effects: readonly Readonly<ActiveEffect>[],
  kind: TimedEffectKind,
  now: number,
): boolean {
  return effects.some((effect) => effect.kind === kind && effect.until > now);
}

/** A spun board is rendered upside down; pointer hits map to the tile really under the finger. */
export function spunTile(index: number, size: BoardSize): number {
  return index < 0 ? index : size * size - 1 - index;
}

/** Under fog only the head of the trace and its neighbours are legible; nothing when idle. */
export function foggedTiles(head: number | undefined, size: BoardSize): boolean[] {
  const visible = new Set<number>();
  if (head !== undefined) {
    visible.add(head);
    for (const neighbor of neighborsFor(size)[head] ?? []) visible.add(neighbor);
  }
  return Array.from({ length: size * size }, (_, index) => !visible.has(index));
}

/** One line of copy for a consumed power-up from this browser's point of view. */
export function powerUpAnnouncement(
  message: Pick<PowerUpMessage, 'kind' | 'by' | 'targets' | 'shielded'>,
  meId: string,
  nameOf: (id: string) => string,
): string {
  const info = POWER_UP_INFO[message.kind];
  const mine = message.by === meId;
  const who = mine ? 'You' : nameOf(message.by);
  if (!isDebuffKind(message.kind)) return `${who} took ${info.title}!`;
  if (message.shielded.includes(meId)) return `Your shield blocked ${nameOf(message.by)}’s ${info.title}!`;
  if (message.targets.includes(meId)) return `${nameOf(message.by)} hit you with ${info.title}!`;
  return `${who} hit everyone with ${info.title}!`;
}

export type PowerUpFeedbackOutcome = 'collected' | 'blocked' | 'hit';

/** Only events affecting this player receive a visual/audio cue; bystanders stay quiet. */
export function powerUpFeedbackOutcome(
  message: Pick<PowerUpMessage, 'kind' | 'by' | 'targets' | 'shielded'>,
  meId: string,
): PowerUpFeedbackOutcome | null {
  if (message.by === meId) return 'collected';
  if (message.shielded.includes(meId)) return 'blocked';
  if (isDebuffKind(message.kind) && message.targets.includes(meId)) return 'hit';
  return null;
}

/**
 * The theme cue this browser hears: the kind's own sound for the player who took it, a hit
 * when a debuff lands on you, a block when your shield absorbed it, nothing as a bystander.
 */
export function powerUpCue(
  message: Pick<PowerUpMessage, 'kind' | 'by' | 'targets' | 'shielded'>,
  meId: string,
): SoundEvent | null {
  switch (powerUpFeedbackOutcome(message, meId)) {
    case 'collected':
      return powerUpSoundEvent(message.kind);
    case 'blocked':
      return 'powerUpBlocked';
    case 'hit':
      return 'powerUpHit';
    default:
      return null;
  }
}

/** Inline `--pu-*` bindings so one board rule styles any kind from the active theme's tokens. */
export function powerUpTileStyle(kind: PowerUpKind): string {
  return POWER_UP_TILE_PROPERTIES.map(
    (property) => `--pu-${kebab(property)}:var(${powerUpTileProperty(kind, property)})`,
  ).join(';');
}
